import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import { useCategories } from '../hooks/useCategories';
import './AddExpenseModal.css';

function AddExpenseModal({ onClose }) {
  const { wallets, selectedWallets, refreshWallets } = useWallets();
  const { categories, loading: categoriesLoading } = useCategories();
  
  // Household split state
  const [isHouseholdWallet, setIsHouseholdWallet] = useState(false);
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [householdId, setHouseholdId] = useState(null);
  const [splits, setSplits] = useState([]);
  
  const [formData, setFormData] = useState({
    wallet_id: '',
    date: new Date().toISOString().split('T')[0],
    budget_group: '',
    category: '',
    subcategory: '',
    description: '',
    amount: '',
    currency: 'EUR',
    notes: '',
    // Split fields for household wallets
    split_type: 'equal',
    payment_mode: 'shared',
    paid_by_user_id: null
  });

  useEffect(() => {
    // Set default wallet to first selected wallet
    if (wallets.length > 0 && selectedWallets.length > 0) {
      const defaultWallet = wallets.find(w => w.id === selectedWallets[0]);
      
      if (defaultWallet) {
        setFormData(prev => ({
          ...prev,
          wallet_id: defaultWallet.id,
          currency: defaultWallet.currency
        }));
      }
    }
  }, [wallets, selectedWallets]);

  // Detect household wallet and load members
  useEffect(() => {
    if (formData.wallet_id) {
      const wallet = wallets.find(w => w.id === parseInt(formData.wallet_id));
      if (wallet && wallet.wallet_type === 'household' && wallet.household_id) {
        setIsHouseholdWallet(true);
        setHouseholdId(wallet.household_id);
        loadHouseholdMembers(wallet.household_id);
      } else {
        setIsHouseholdWallet(false);
        setHouseholdId(null);
        setHouseholdMembers([]);
        setSplits([]);
      }
    } else {
      setIsHouseholdWallet(false);
      setHouseholdId(null);
      setHouseholdMembers([]);
      setSplits([]);
    }
  }, [formData.wallet_id, wallets]);

  // Recalculate splits when amount or split_type changes
  useEffect(() => {
    if (formData.amount && householdMembers.length > 0 && isHouseholdWallet) {
      calculateSplits();
    }
  }, [formData.amount, formData.split_type, householdMembers, isHouseholdWallet]);

  const loadHouseholdMembers = async (householdId) => {
    try {
      const response = await api.get(`/household/${householdId}/members`);
      setHouseholdMembers(response.data);
      calculateInitialSplits(response.data);
    } catch (error) {
      console.error('Error loading household members:', error);
    }
  };

  const calculateInitialSplits = (membersList) => {
    const initialSplits = membersList.map(member => ({
      user_id: member.user_id,
      user_name: member.name,
      split_amount: 0,
      split_percentage: 100 / membersList.length
    }));
    setSplits(initialSplits);
  };

  const calculateSplits = () => {
    const amount = parseFloat(formData.amount) || 0;
    
    if (formData.split_type === 'equal') {
      const equalAmount = amount / householdMembers.length;
      setSplits(householdMembers.map(member => ({
        user_id: member.user_id,
        user_name: member.name,
        split_amount: equalAmount,
        split_percentage: 100 / householdMembers.length
      })));
    } else if (formData.split_type === 'percentage') {
      setSplits(prev => prev.map(split => ({
        ...split,
        split_amount: amount * (split.split_percentage / 100)
      })));
    }
  };

  const handleSplitTypeChange = (type) => {
    setFormData({ ...formData, split_type: type });
    if (type === 'custom') {
      const amount = parseFloat(formData.amount) || 0;
      const equalAmount = amount / householdMembers.length;
      setSplits(householdMembers.map(member => ({
        user_id: member.user_id,
        user_name: member.name,
        split_amount: equalAmount,
        split_percentage: 100 / householdMembers.length
      })));
    }
  };

  const handlePercentageChange = (userId, percentage) => {
    const newPercentage = parseFloat(percentage) || 0;
    setSplits(prev => prev.map(split => 
      split.user_id === userId 
        ? { 
            ...split, 
            split_percentage: newPercentage,
            split_amount: (parseFloat(formData.amount) || 0) * (newPercentage / 100)
          }
        : split
    ));
  };

  const handleCustomAmountChange = (userId, amount) => {
    const newAmount = parseFloat(amount) || 0;
    setSplits(prev => prev.map(split =>
      split.user_id === userId
        ? { ...split, split_amount: newAmount }
        : split
    ));
  };

  const validateSplits = () => {
    if (!isHouseholdWallet) {
      return true;
    }

    const totalAmount = parseFloat(formData.amount) || 0;
    
    if (formData.split_type === 'percentage') {
      const totalPercentage = splits.reduce((sum, split) => sum + (split.split_percentage || 0), 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        alert(`Percentages must add up to 100% (currently ${totalPercentage.toFixed(1)}%)`);
        return false;
      }
    } else if (formData.split_type === 'custom') {
      const totalSplit = splits.reduce((sum, split) => sum + (split.split_amount || 0), 0);
      if (Math.abs(totalSplit - totalAmount) > 0.01) {
        alert(`Split amounts must equal total (${totalSplit.toFixed(2)} ≠ ${totalAmount.toFixed(2)})`);
        return false;
      }
    }
    
    return true;
  };

  const getTotalSplit = () => {
    return splits.reduce((sum, split) => sum + (split.split_amount || 0), 0);
  };

  const getTotalPercentage = () => {
    return splits.reduce((sum, split) => sum + (split.split_percentage || 0), 0);
  };

  const handleWalletChange = (e) => {
    const walletId = parseInt(e.target.value);
    const wallet = wallets.find(w => w.id === walletId);
    setFormData({
      ...formData,
      wallet_id: walletId,
      currency: wallet?.currency || 'EUR'
    });
  };

  const handleBudgetGroupChange = (e) => {
    const budgetGroup = e.target.value;
    setFormData({
      ...formData,
      budget_group: budgetGroup,
      category: '',
      subcategory: ''
    });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    const subcategories = categories[category]?.subcategories || [];
    setFormData({
      ...formData,
      category,
      subcategory: subcategories[0] || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSplits()) {
      return;
    }
    
    try {
      if (isHouseholdWallet) {
        // Create household expense with splits
        const payload = {
          date: formData.date,
          category: formData.category,
          subcategory: formData.subcategory,
          description: formData.description,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          split_type: formData.split_type,
          payment_mode: formData.payment_mode,
          paid_by_user_id: formData.payment_mode === 'paid_by_other' ? formData.paid_by_user_id : null,
          notes: formData.notes,
          splits: splits.map(split => ({
            user_id: split.user_id,
            split_amount: split.split_amount,
            split_percentage: formData.split_type === 'percentage' ? split.split_percentage : null
          }))
        };

        await api.post(`/household/${householdId}/expenses/with-splits`, payload);
      } else {
        // Create personal expense
        await api.post('/personal/expenses', {
          wallet_id: parseInt(formData.wallet_id),
          date: formData.date,
          category: formData.category,
          subcategory: formData.subcategory,
          description: formData.description,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          notes: formData.notes
        });
      }
      
      await refreshWallets();
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error creating expense:', error);
      alert('Failed to create expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const getFilteredCategories = () => {
    if (!formData.budget_group) {
      return Object.keys(categories).sort();
    }
    
    return Object.keys(categories)
      .filter(cat => categories[cat].budget_group === formData.budget_group)
      .sort();
  };

  const filteredCategoryKeys = getFilteredCategories();
  const currentSubcategories = formData.category ? (categories[formData.category]?.subcategories || []) : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>💸 Add Expense</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Pot *</label>
            <select value={formData.wallet_id} onChange={handleWalletChange} required>
              <option value="">Select pot...</option>
              {wallets.map(wallet => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.icon || '🫙'} {wallet.name} ({wallet.currency})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Budget Group *</label>
            <select 
              value={formData.budget_group} 
              onChange={handleBudgetGroupChange} 
              required
              disabled={categoriesLoading}
            >
              <option value="">Select budget group...</option>
              <option value="needs">🏠 Needs</option>
              <option value="wants">🎉 Wants</option>
              <option value="savings">💰 Savings</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category *</label>
              <select 
                value={formData.category} 
                onChange={handleCategoryChange} 
                required 
                disabled={categoriesLoading || !formData.budget_group}
              >
                <option value="">
                  {!formData.budget_group ? 'Select budget group first...' : 'Select category...'}
                </option>
                {filteredCategoryKeys.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Subcategory *</label>
              <select 
                value={formData.subcategory} 
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                disabled={!formData.category}
                required
              >
                <option value="">Select subcategory...</option>
                {currentSubcategories.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Weekly groceries at Tesco (optional)"
            />
          </div>

          <div className="form-group">
            <label>Amount ({getCurrencySymbol(formData.currency)}) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Optional notes..."
            />
          </div>

          {/* Show splits ONLY for household wallets */}
          {isHouseholdWallet && (
            <>
              {/* Payment Mode Section */}
              <div className="form-section">
                <h3>💳 Payment Mode</h3>
                <div className="payment-mode-selector">
                  <label className={`mode-option ${formData.payment_mode === 'shared' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="shared"
                      checked={formData.payment_mode === 'shared'}
                      onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    />
                    <div>
                      <span className="mode-icon">🏦</span>
                      <span className="mode-label">Paid from Shared Account</span>
                      <span className="mode-desc">No debt created</span>
                    </div>
                  </label>
                  <label className={`mode-option ${formData.payment_mode === 'paid_by_me' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="paid_by_me"
                      checked={formData.payment_mode === 'paid_by_me'}
                      onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    />
                    <div>
                      <span className="mode-icon">👤</span>
                      <span className="mode-label">I Paid for Everyone</span>
                      <span className="mode-desc">Others owe me their share</span>
                    </div>
                  </label>
                  <label className={`mode-option ${formData.payment_mode === 'paid_by_other' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="paid_by_other"
                      checked={formData.payment_mode === 'paid_by_other'}
                      onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    />
                    <div>
                      <span className="mode-icon">👥</span>
                      <span className="mode-label">Someone Else Paid</span>
                      <span className="mode-desc">I owe them my share</span>
                    </div>
                  </label>
                </div>

                {formData.payment_mode === 'paid_by_other' && (
                  <div className="form-group">
                    <label>Who Paid? *</label>
                    <select
                      value={formData.paid_by_user_id || ''}
                      onChange={(e) => setFormData({ ...formData, paid_by_user_id: parseInt(e.target.value) })}
                      required
                    >
                      <option value="">Select member...</option>
                      {householdMembers.map(member => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Split Type Section */}
              <div className="form-section">
                <h3>✂️ Split Type</h3>
                <div className="split-type-selector">
                  <button
                    type="button"
                    className={`split-type-btn ${formData.split_type === 'equal' ? 'selected' : ''}`}
                    onClick={() => handleSplitTypeChange('equal')}
                  >
                    <span className="split-icon">⚖️</span>
                    <span>Equal Split</span>
                  </button>
                  <button
                    type="button"
                    className={`split-type-btn ${formData.split_type === 'percentage' ? 'selected' : ''}`}
                    onClick={() => handleSplitTypeChange('percentage')}
                  >
                    <span className="split-icon">%</span>
                    <span>Percentage</span>
                  </button>
                  <button
                    type="button"
                    className={`split-type-btn ${formData.split_type === 'custom' ? 'selected' : ''}`}
                    onClick={() => handleSplitTypeChange('custom')}
                  >
                    <span className="split-icon">✏️</span>
                    <span>Custom Amounts</span>
                  </button>
                </div>
              </div>

              {/* Split Breakdown */}
              <div className="split-preview">
                <h4>Split Breakdown</h4>
                <div className="split-list">
                  {splits.map(split => (
                    <div key={split.user_id} className="split-item">
                      <div className="split-member">{split.user_name}</div>
                      <div className="split-inputs">
                        {formData.split_type === 'equal' && (
                          <span className="split-amount">
                            {getCurrencySymbol(formData.currency)}{split.split_amount.toFixed(2)}
                          </span>
                        )}
                        {formData.split_type === 'percentage' && (
                          <>
                            <input
                              type="number"
                              step="0.1"
                              value={split.split_percentage}
                              onChange={(e) => handlePercentageChange(split.user_id, e.target.value)}
                              className="percentage-input"
                            />
                            <span className="percentage-symbol">%</span>
                            <span className="split-amount">
                              {getCurrencySymbol(formData.currency)}{split.split_amount.toFixed(2)}
                            </span>
                          </>
                        )}
                        {formData.split_type === 'custom' && (
                          <>
                            <span className="currency-symbol">{getCurrencySymbol(formData.currency)}</span>
                            <input
                              type="number"
                              step="0.01"
                              value={split.split_amount}
                              onChange={(e) => handleCustomAmountChange(split.user_id, e.target.value)}
                              className="amount-input"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="split-totals">
                  {formData.split_type === 'percentage' && (
                    <div className={`split-total ${Math.abs(getTotalPercentage() - 100) < 0.01 ? 'valid' : 'invalid'}`}>
                      <span>Total Percentage:</span>
                      <span>{getTotalPercentage().toFixed(1)}%</span>
                    </div>
                  )}
                  {formData.split_type === 'custom' && (
                    <div className={`split-total ${Math.abs(getTotalSplit() - parseFloat(formData.amount || 0)) < 0.01 ? 'valid' : 'invalid'}`}>
                      <span>Total Split:</span>
                      <span>{getCurrencySymbol(formData.currency)}{getTotalSplit().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="expense-amount">
                    <span>Expense Amount:</span>
                    <span>{getCurrencySymbol(formData.currency)}{parseFloat(formData.amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary">Add Expense</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddExpenseModal;
